import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { IS_IOS, IV_THEME_MODES, SELECTORS, STORAGE_NAMES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { BackgroundConfig } from '@root/app.interfaces';
import { EngineService } from '@core/services/engine/engine.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { Subscription } from 'rxjs';
import { BackgroundService } from '@core/services/background-service/background.service';

@Component({
	selector: 'app-iv-theme-switcher',
	templateUrl: './iv-theme-switcher.component.html',
	styleUrls: ['./iv-theme-switcher.component.scss', './iv-theme-switcher.responsive.scss'],
})
export class IvThemeSwitcherComponent implements OnInit, OnDestroy {
	@Input() prodStatus: boolean;

	getViewType: Subscription;

	isMobile = this.passDataService.isMobileOrTablet;
	themeModes = IV_THEME_MODES;
	activeMode = IV_THEME_MODES.day;

	constructor(
		private shareService: ShareService,
		private engineService: EngineService,
		private passDataService: PassDataService,
		private backgroundService: BackgroundService,
		private sessionStorageService: SessionStorageService,
	) {}

	ngOnInit(): void {
		this.setCurrentBackgroundTheme();
		this.onChangeViewType();
	}

	ngOnDestroy() {
		this.getViewType.unsubscribe();
	}

	onSwitchThemeMode(mode: string): void {
		this.activeMode = mode;
		this.backgroundService.backgroundTheme = mode;

		const canvas = document.getElementById(SELECTORS.render_canvas);
		const { src, night_src, width, height } = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
		const backgroundUrl = mode === this.themeModes.day ? src : night_src;

		this.callBackgroundImageAnimation(canvas, backgroundUrl, width, height);
		this.engineService.toggleIVLightTheme(this.activeMode);
	}

	callBackgroundImageAnimation(element: HTMLElement, imageUrl: string, imageWidth: number, imageHeight: number, duration = 300) {
		const image = new Image();
		image.src = imageUrl;
		image.onload = () => {
			if (IS_IOS()) {
				this.fadeBackgroundImage(element, imageUrl, imageWidth, imageHeight, duration);
			} else {
				element.style.transition = `background-image ${duration}ms ease-in-out`;
				element.style.backgroundImage = `url(${imageUrl})`;
				setTimeout(() => (element.style.transition = ''), duration);
			}
		};
	}

	fadeBackgroundImage(element: HTMLElement, imageUrl: string, imageWidth: number, imageHeight: number, duration = 300) {
		const startTime = performance.now();
		const color = this.activeMode === IV_THEME_MODES.day ? '195, 220, 235' : '5, 15, 45';
		const emptySpace = this.calculateEmptySpace(element, imageWidth, imageHeight);
		let opacity = 0;

		function update() {
			const elapsed = performance.now() - startTime;
			opacity = Math.min(elapsed / duration, 1);

			element.style.backgroundImage = `linear-gradient(90deg,
			rgba(0, 0, 0, 0) ${emptySpace}%,
			rgba(${color}, ${1 - opacity}) ${emptySpace}%, 
			rgba(${color}, ${1 - opacity}) ${100 - emptySpace}%, 
			rgba(0, 0, 0, 0) ${100 - emptySpace}%),url(${imageUrl})`;

			if (opacity < 1) {
				requestAnimationFrame(update);
			} else {
				element.style.backgroundImage = `url(${imageUrl})`;
			}
		}

		requestAnimationFrame(update);
	}

	calculateEmptySpace(element: HTMLElement, imageWidth: number, imageHeight: number): number {
		const canvasWidth = element.clientWidth;
		const canvasHeight = element.clientHeight;

		const scaleFactor = canvasHeight / imageHeight;
		const newImgWidth = imageWidth * scaleFactor;

		const emptySpace = (canvasWidth - newImgWidth) / 2;
		return (emptySpace / canvasWidth) * 100;
	}

	setCurrentBackgroundTheme(): void {
		const ivCurrentBackground: BackgroundConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
		this.activeMode = ivCurrentBackground.theme;
		this.backgroundService.backgroundTheme = ivCurrentBackground.theme;
	}

	onChangeViewType(): void {
		this.getViewType = this.shareService.getViewType.subscribe(() => this.setCurrentBackgroundTheme());
	}
}
